import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Route, Switch, withRouter } from 'react-router-dom';

import Async from './hoc/AsyncComponent';
import Authenticated from './hoc/AuthenticatedComponent';
import { isMemberOfGroup } from './helpers';
import { GROUP_IDS } from './constants';

// Dashboard
const Home = Async(() => import(/* webpackChunkName: "home" */ './pages/Home'));

// Login
const Login = Async(() =>
	import(/* webpackChunkName: "login" */ './pages/Login')
);

// Forgot password
const ForgotPassword = Async(() =>
	import(/* webpackChunkName: "forgot_password" */ './pages/ForgotPassword')
);

// Reset password
const ResetPassword = Async(() =>
	import(/* webpackChunkName: "reset_password" */ './pages/ResetPassword')
);

// 404
const NotFound = Async(() =>
	import(/* webpackChunkName: "not_found" */ './pages/NotFound')
);

const Admin = Async(() =>
	import(/* webpackChunkName: "admin" */ './pages/Admin')
);

// Users
const Users = Async(() =>
	import(/* webpackChunkName: "users" */ './pages/Users')
);
const UserEdit = Async(() =>
	import(/* webpackChunkName: "user_edit" */ './pages/UserEdit')
);
const UserAdd = Async(() =>
	import(/* webpackChunkName: "user_create" */ './pages/UserAdd')
);

// Pages
const Page = Async(() => import(/* webpackChunkName: "page" */ './pages/Page'));
const Pages = Async(() =>
	import(/* webpackChunkName: "pages" */ './pages/Pages')
);
const PageEdit = Async(() =>
	import(/* webpackChunkName: "page_edit" */ './pages/PageEdit')
);

// Groups
const Groups = Async(() =>
	import(/* webpackChunkName: "groups" */ './pages/Groups')
);
const Group = Async(() =>
	import(/* webpackChunkName: "group" */ './pages/Group')
);

// Admin notifications
const AdminNotifications = Async(() =>
	import(/* webpackChunkName: "admin_notifications" */ './pages/AdminNotifications')
);
const AdminNotificationEdit = Async(() =>
	import(/* webpackChunkName: "admin_notification_edit" */ './pages/AdminNotificationEdit')
);
const AdminNotificationAdd = Async(() =>
	import(/* webpackChunkName: "admin_notification_create" */ './pages/AdminNotificationAdd')
);

// Product information sheets
const PISheets = Async(() =>
	import(/* webpackChunkName: "pi_sheets" */ './pages/PISheets')
);
const PISheetEdit = Async(() =>
	import(/* webpackChunkName: "pi_sheet_edit" */ './pages/PISheetEdit')
);

// Bulletins
const Bulletins = Async(() =>
	import(/* webpackChunkName: "bulletins" */ './pages/Bulletins')
);
const Bulletin = Async(() =>
	import(/* webpackChunkName: "bulletin" */ './pages/Bulletin')
);

// CR
const CR = Async(() => import(/* webpackChunkName: "cr_edit" */ './pages/CR'));
const CRs = Async(() => import(/* webpackChunkName: "crs" */ './pages/CRs'));
const CommissioningRequestForm = Async(() =>
	import(/* webpackChunkName: "commissioning_request_form" */ './pages/CommissioningRequestForm')
);

// QSR
const QSRs = Async(() => import(/* webpackChunkName: "qsrs" */ './pages/QSRs'));
const QSR = Async(() => import(/* webpackChunkName: "qsr" */ './pages/QSR'));
const QSRPublished = Async(() =>
	import(/* webpackChunkName: "qsr_published" */ './pages/QSRPublished')
);
const QSRSearch = Async(() =>
	import(/* webpackChunkName: "qsr_search" */ './pages/QSRSearch')
);
const QSROverview = Async(() =>
	import(/* webpackChunkName: "qsr_overview" */ './pages/QSROverview')
);

// End user serial numbers
const EndUserSerialNumbers = Async(() =>
	import(/* webpackChunkName: "end_user_serials" */ './pages/EndUserSerialNumbers')
);

//OEM
const AdminOems = Async(() =>
	import(/* webpackChunkName: "admin_notification_create" */ './pages/AdminOems')
);

// Spreader manuals
const SpreaderManualSearch = Async(() =>
	import(/* webpackChunkName: "spreader_manual_search" */ './pages/SpreaderManualSearch')
);

// Search
const Search = Async(() =>
	import(/* webpackChunkName: "search" */ './pages/Search')
);

// Browse
const Browse = Async(() =>
	import(/* webpackChunkName: "browse" */ './pages/Browse')
);
const PublicBrowse = Async(() =>
	import(/* webpackChunkName: "browse_public" */ './pages/Browse/PublicBrowse')
);

// Bromma Sourcesafe
const SourcesafeSerials = Async(() =>
	import(/* webpackChunkName */ './pages/SourcesafeSerials')
);
const SourcesafeSerial = Async(() =>
	import(/* webpackChunkName */ './pages/SourcesafeSerial')
);

const Routes = ({ auth }) => (
	<Switch>
		<Route exact path="/" component={Authenticated(Home)} />

		<Route
			exact
			path="/admin"
			component={Authenticated(Admin, [['user', 'write']])}
		/>

		<Route
			exact
			path="/users"
			component={Authenticated(Users, [['user', 'write']])}
		/>
		<Route
			path="/users/edit/:id"
			component={Authenticated(UserEdit, [['user', 'write']], true)}
		/>
		<Route
			path="/users/add"
			component={Authenticated(UserAdd, [['user', 'write']])}
		/>

		<Route
			exact
			path="/pages"
			component={Authenticated(Pages, [['page', 'write']])}
		/>
		<Route
			path="/pages/add"
			component={Authenticated(PageEdit, [['page', 'write']])}
		/>
		<Route
			path="/pages/edit/:id"
			component={Authenticated(PageEdit, [['page', 'write']], true)}
		/>

		<Route
			exact
			path="/groups"
			component={Authenticated(Groups, [['group', 'write']])}
		/>
		<Route
			path="/groups/add"
			component={Authenticated(Group, [['group', 'write']])}
		/>
		<Route
			path="/groups/edit/:id"
			component={Authenticated(Group, [['group', 'write']])}
		/>

		<Route exact path="/crs" component={Authenticated(CRs, [['cr', 'read']])} />
		<Route
			exact
			path="/crs/add"
			component={Authenticated(CR, [['cr', 'write']])}
		/>
		<Route
			path="/crs/edit/:id"
			component={Authenticated(CR, [['cr', 'write']])}
		/>
		<Route
			path="/crs/commissioning-request-form"
			component={Authenticated(CommissioningRequestForm, [['cr', 'read']])}
		/>

		<Route
			exact
			path="/qsrs"
			component={Authenticated(QSRs, [
				['qsr', 'read'],
				isMemberOfGroup(auth, GROUP_IDS.externalReader),
			])}
		/>
		<Route
			path="/qsrs/add"
			component={Authenticated(QSR, [['qsr', 'create']])}
		/>
		<Route
			path="/qsrs/edit/:id"
			component={Authenticated(QSR, [
				['qsr', 'read'],
				isMemberOfGroup(auth, GROUP_IDS.externalReader),
			])}
		/>
		<Route
			path="/qsrs/published/:id"
			component={Authenticated(QSRPublished, [['qsr', 'read']])}
		/>
		<Route
			path="/qsrs/search"
			component={Authenticated(QSRSearch, [['qsr', 'read']])}
		/>
		<Route
			exact
			path="/qsrs/overview"
			component={Authenticated(QSROverview, [['qsr', 'read']])}
		/>

		<Route
			exact
			path="/admin-notifications"
			component={Authenticated(AdminNotifications, [
				['admin_notification', 'write'],
			])}
		/>
		<Route
			path="/admin-notifications/add"
			component={Authenticated(AdminNotificationAdd, [
				['admin_notification', 'write'],
			])}
		/>
		<Route
			path="/admin-notifications/edit/:id"
			component={Authenticated(AdminNotificationEdit, [
				['admin_notification', 'write'],
			])}
		/>

		<Route
			exact
			path="/after-sales/service-bulletins"
			component={Authenticated(Bulletins, [['bulletin', 'read']])}
		/>
		<Route
			path="/after-sales/service-bulletins/add"
			component={Authenticated(Bulletin, [['bulletin', 'create']])}
		/>
		<Route
			path="/after-sales/service-bulletins/edit/:id"
			component={Authenticated(Bulletin, [['bulletin', 'create']])}
		/>
		<Route
			path="/after-sales/:id"
			component={Authenticated(Page, [['page', 'read']])}
		/>

		<Route
			path="/sales-support/:id"
			component={Authenticated(Page, [['page', 'read']])}
		/>

		<Route
			path="/product-information/pi-sheets/add"
			component={Authenticated(PISheetEdit, [['product_information', 'write']])}
		/>
		<Route
			path="/product-information/pi-sheets/edit/:id"
			component={Authenticated(PISheetEdit, [['product_information', 'write']])}
		/>
		<Route
			path="/product-information/pi-sheets/:categoryId?"
			exact
			component={Authenticated(PISheets, [['product_information', 'read']])}
		/>

		<Route
			path="/product-information/:id"
			component={Authenticated(Page, [['page', 'read']])}
		/>

		<Route
			path="/corporate-information/:id"
			component={Authenticated(Page, [['page', 'read']])}
		/>

		<Route
			path="/end-user-serial-numbers"
			component={Authenticated(EndUserSerialNumbers, [
				['end_user_serial_number', 'write'],
			])}
		/>

		<Route
			path="/admin-oems"
			component={Authenticated(AdminOems, [['oems', 'write']])}
		/>

		<Route
			path="/spreader-manuals/search"
			component={Authenticated(SpreaderManualSearch)}
		/>

		<Route path="/search" component={Authenticated(Search)} />

		<Route
			path="/browse/:topic/:id/:folderToken?"
			component={Authenticated(Browse, [
				['folder', 'read'],
				['spreader_document', 'read'],
			])}
		/>

		<Route
			exact
			path="/sourcesafe"
			component={Authenticated(SourcesafeSerials, [['sourcesafe', 'read']])}
		/>
		<Route
			path="/sourcesafe/:serialNumber"
			component={Authenticated(SourcesafeSerial, [['sourcesafe', 'read']])}
		/>

		<Route
			exact
			path="/shared-folder/:folderToken/:folderId?"
			component={PublicBrowse}
		/>

		<Route exact path="/login" component={Login} />
		<Route exact path="/forgot-password" component={ForgotPassword} />
		<Route exact path="/reset-password" component={ResetPassword} />
		<Route component={NotFound} />
	</Switch>
);

const mapState = state => ({
	auth: state.auth,
});

export default compose(withRouter, connect(mapState))(Routes);



// WEBPACK FOOTER //
// ./src/Routes.js